import { defineMessages, useIntl } from 'react-intl';

import { useContentfulContentTypeEntry } from '@order/Contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useCartHandoffContent = (params: CartHandoffContentParams) => {
  const {
    isDismissed,
    isHandingOffCart,
    isNothingAvailable,
    isInConflictsWarning,
    isInActiveBagWarning,
    conflicts,
  } = params;

  const { formatMessage, formatList } = useIntl();
  const contentfulData = useContentfulContentTypeEntry<ContentfulEntry>({ contentType: 'cartHandoff' }); // prettier-ignore
  const content = contentfulData.data?.fields;

  if (isDismissed) {
    return {
      title: undefined,
      body: undefined,
      cta: formatMessage(messages.close),
    };
  }

  if (isNothingAvailable && !isHandingOffCart) {
    return {
      title: content?.unavailableTitle ?? formatMessage(messages.unavailableTitle),
      body: isInActiveBagWarning
        ? (content?.unavailableBodyWithBag ?? formatMessage(messages.unavailableBodyWithBag))
        : (content?.unavailableBodyWithoutBag ?? formatMessage(messages.unavailableBodyWithoutBag)),
      cta: content?.unavailableCta ?? formatMessage(messages.unavailableCta),
    };
  }

  if (isInConflictsWarning && !isHandingOffCart) {
    return {
      title: content?.conflictsTitle ?? formatMessage(messages.conflictsTitle),
      body:
        conflicts?.length === 1
          ? (content?.conflictsBodySingle.replaceAll('{conflict}', conflicts[0]) ??
            formatMessage(messages.conflictsBodySingle, { conflict: conflicts[0] }))
          : (content?.conflictsBodyMultiple.replaceAll('{conflicts}', formatList(conflicts)) ??
            formatMessage(messages.conflictsBodyMultiple, { conflicts: formatList(conflicts) })),
      cta: content?.conflictsCta ?? formatMessage(messages.conflictsCta),
    };
  }

  if (isInActiveBagWarning && !isHandingOffCart) {
    return {
      title: content?.activeBagTitle ?? formatMessage(messages.activeBagTitle),
      body: content?.activeBagBody ?? formatMessage(messages.activeBagBody),
      cta: content?.activeBagCta ?? formatMessage(messages.activeBagCta),
    };
  }

  return {
    title: content?.defaultTitle ?? formatMessage(messages.defaultTitle),
    body: undefined,
    cta: formatMessage(messages.close),
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  defaultTitle: {
    defaultMessage: 'Setting up your order...',
    description: 'Cart Handoff | Default | Title',
  },
  activeBagTitle: {
    defaultMessage: 'You have an active bag',
    description: 'Cart Handoff | Active Bag | Title',
  },
  activeBagBody: {
    defaultMessage: 'Proceeding with a pre-made order will clear your current bag.',
    description: 'Cart Handoff | Active Bag | Body',
  },
  activeBagCta: {
    defaultMessage: 'Clear bag & continue',
    description: 'Cart Handoff | Active Bag | CTA',
  },
  conflictsTitle: {
    defaultMessage: 'Partial availability',
    description: 'Cart Handoff | Conflicts | Body',
  },
  conflictsBodySingle: {
    defaultMessage: '{conflict} is not available and will not be added to your bag.',
    description: 'Cart Handoff | One missing conflict',
  },
  conflictsBodyMultiple: {
    defaultMessage: '{conflicts} are not available and will not be added to your bag.',
    description: 'Cart Handoff | Many missing conflicts',
  },
  conflictsCta: {
    defaultMessage: 'Continue',
    description: 'Cart Handoff | Conflicts | CTA',
  },
  unavailableTitle: {
    defaultMessage: 'Something went wrong',
    description: 'Cart Handoff | Unavailable | Title',
  },
  unavailableBodyWithBag: {
    defaultMessage: "We couldn't recreate your order. Your current bag has not been cleared.",
    description: 'Cart Handoff | Unavailable | Body',
  },
  unavailableBodyWithoutBag: {
    defaultMessage: `We couldn't recreate your order. Please try again or head to the menu and build a new order.`,
    description: 'Cart Handoff | Unavailable | Body',
  },
  unavailableCta: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
  close: {
    defaultMessage: 'Close',
    description: 'Cart Handoff | Close',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CartHandoffContentParams = {
  isDismissed: boolean;
  isHandingOffCart: boolean;
  isNothingAvailable: boolean;
  isInConflictsWarning: boolean;
  isInActiveBagWarning: boolean;
  conflicts: string[];
};

type ContentfulEntry = {
  defaultTitle: string;
  activeBagTitle: string;
  activeBagBody: string;
  activeBagCta: string;
  conflictsTitle: string;
  conflictsBodySingle: string;
  conflictsBodyMultiple: string;
  conflictsCta: string;
  unavailableTitle: string;
  unavailableBodyWithBag: string;
  unavailableBodyWithoutBag: string;
  unavailableCta: string;
};
